import type { components } from '@/generated/api/schema';

/**
 * Shared short-polling interval for the three sicht list queries and for the order-detail
 * comment chat (`useAuftragKommentare`). Lives here — the shared query-key infrastructure file
 * for the three sicht hooks — rather than in `useAuftragKommentare.ts`, so the list hooks do not
 * need to import a fachfremde (off-topic) chat hook just to read a constant they share with it.
 * Value stays `60_000` (60 s); do **not** introduce a second constant with the same value for
 * either consumer — that would let the two intervals silently drift apart over time.
 */
export const KOMMENTARE_POLL_INTERVALL_MS = 60_000;

/** `type` filter alias — the shared OpenAPI enum for order discipline. */
type AuftragTyp = components['schemas']['AuftragTyp'];

/** `status` filter alias — the shared OpenAPI enum for order lifecycle status. */
type AuftragStatus = components['schemas']['AuftragStatus'];

/**
 * Shared parameter shape used by all three view-specific query-key factories.
 *
 * Includes the column-filter fields introduced for server-side filtering/sorting
 * (`status`, the `createdAt`/`desiredDeliveryAt` ranges) alongside the original
 * pagination/sort/type params — every distinct combination is its own cache entry.
 */
interface AuftraegeKeyParams {
  readonly page: number;
  readonly size: number;
  readonly sort: string;
  readonly types: readonly AuftragTyp[] | undefined;
  /** Single-value status filter (AK-9 — no repeatable/OR semantics). */
  readonly status?: AuftragStatus | undefined;
  /** ISO-8601 calendar date (`yyyy-MM-dd`), inclusive lower bound on `createdAt`. */
  readonly createdAtFrom?: string | undefined;
  /** ISO-8601 calendar date (`yyyy-MM-dd`), inclusive upper bound on `createdAt`. */
  readonly createdAtTo?: string | undefined;
  /** ISO-8601 calendar date (`yyyy-MM-dd`), inclusive lower bound on `desiredDeliveryAt`. */
  readonly desiredDeliveryAtFrom?: string | undefined;
  /** ISO-8601 calendar date (`yyyy-MM-dd`), inclusive upper bound on `desiredDeliveryAt`. */
  readonly desiredDeliveryAtTo?: string | undefined;
  /**
   * Portal-Sicht-only time filter — ISO-8601 instant; `undefined` for the Praxis/DelaboLabor
   * sichten and for the Portal sicht when no cutoff is applied.
   */
  readonly statusChangedBefore?: string | undefined;
  /**
   * Role-dependent full-text search term (already debounced by the caller). `undefined` when no
   * search is active.
   */
  readonly search?: string | undefined;
}

/**
 * TanStack Query key builders for the `auftraege` feature.
 *
 * Each sicht (view) has its own top-level key segment to guarantee cache
 * isolation — a Praxis query never cross-talks with a DelaboLabor or Portal
 * query even when page/size/sort params match.
 */
export const auftraegeKeys = {
  /** Root key for all Auftraege queries — use to invalidate the entire feature cache. */
  all: ['auftraege'] as const,

  /** All Praxis-sicht list queries. */
  praxisAll: () => [...auftraegeKeys.all, 'praxis'] as const,

  /** Praxis-sicht list query keyed by pagination and filter params. */
  praxis: (params: AuftraegeKeyParams) =>
    [...auftraegeKeys.praxisAll(), params] as const,

  /** All DelaboLabor-sicht list queries. */
  delaboLaborAll: () => [...auftraegeKeys.all, 'delabo-labor'] as const,

  /** DelaboLabor-sicht list query keyed by pagination and filter params. */
  delaboLabor: (params: AuftraegeKeyParams) =>
    [...auftraegeKeys.delaboLaborAll(), params] as const,

  /** All Portal-sicht list queries. */
  portalAll: () => [...auftraegeKeys.all, 'portal'] as const,

  /** Portal-sicht list query keyed by pagination and filter params. */
  portal: (params: AuftraegeKeyParams) =>
    [...auftraegeKeys.portalAll(), params] as const,

  /**
   * Single-order detail query (`GET /api/v1/auftraege/{id}`), used by the Auftragsdaten
   * capture wizard (US-E02-03) to resume/refresh an in-progress draft. Shares the `auftraege`
   * root segment with the list views above so the whole resource can be invalidated together
   * when needed, while the `'detail'` segment keeps per-order caches isolated from any list.
   */
  detail: (auftragId: string) => [...auftraegeKeys.all, 'detail', auftragId] as const,

  /**
   * Belegdaten query (`GET /api/v1/auftraege/{id}/belegdaten`) — the read-only field data for an
   * order's printable documents (Auftragsblatt/Auftragslabel).
   *
   * Deliberately nested *under* {@link auftraegeKeys.detail} rather than sitting beside it as
   * `['auftraege', 'belegdaten', id]`. Belegdaten is a projection of the very same order, so
   * every mutation that changes the order also stales it — but as a sibling key it was a second,
   * independent cache entry that each mutation had to remember to invalidate separately, and
   * none of them did. Saving a charge number and immediately printing then served the
   * pre-mutation document from cache, blank slot and all, without a request on the wire.
   *
   * As a child key, TanStack Query's prefix matching makes `invalidateQueries(detail(id))` cover
   * it, so the existing per-mutation invalidations are correct by construction and a future
   * order mutation cannot reintroduce the bug by forgetting a line. The trailing `'belegdaten'`
   * segment still keeps the two responses (different shapes, fetched independently) in separate
   * entries — the keys differ in length, so they can never collide.
   *
   * Nothing refetches on invalidation: the query has no mounted observer (the PDF buttons fetch
   * it from their click handler), so an order-detail page visit still ships no
   * `patientReference` (PII) for a document the visitor may never download.
   *
   * The optional `phaseId` selects the Aligner-batch variant of the response (it carries `phase`
   * and is fetched with the `phaseId` query parameter). It is its own cache entry — the order-wide
   * response sits under the `'auftrag'` segment — so the two never collide. A UUID can never equal
   * `'auftrag'`.
   */
  belegdaten: (auftragId: string, phaseId?: string) =>
    [...auftraegeKeys.detail(auftragId), 'belegdaten', phaseId ?? 'auftrag'] as const,

  /**
   * Aligner treatment-process query (`GET /api/v1/auftraege/{auftragId}/aligner`) — the single
   * read resource for an ORTHO order's Aligner detail page (treatment plans, batches, control
   * scans, `offeneAktionen`).
   *
   * Nested under {@link auftraegeKeys.detail} for the same reason as {@link
   * auftraegeKeys.belegdaten}: the Aligner process is a projection of the order itself, so any
   * mutation that invalidates `detail(auftragId)` (order-level status changes, etc.) must also
   * stale this cache entry, not leave it serving a snapshot the order-level mutation has already
   * outdated. As a child key, `invalidateQueries(detail(auftragId))`'s prefix match covers it
   * automatically — the many Aligner command hooks that follow in later work-items get this
   * invalidation for free instead of each having to remember a second key.
   */
  alignerProzess: (auftragId: string) =>
    [...auftraegeKeys.detail(auftragId), 'aligner'] as const,

  /**
   * Retainer-order query (`GET /api/v1/auftraege/{auftragId}/aligner/retainer`) — the order's own
   * retainer aggregate (Konzept §4.7 / V-4), deliberately its own resource rather than a field
   * group on the Aligner process.
   *
   * Nested under {@link auftraegeKeys.detail} for the same reason as {@link
   * auftraegeKeys.alignerProzess}: it is a projection of the same order, so any invalidation of
   * `detail(auftragId)` (e.g. after `retainerBeauftragen`) covers it via prefix matching without a
   * second, easy-to-forget invalidation call.
   */
  retainer: (auftragId: string) => [...auftraegeKeys.detail(auftragId), 'aligner-retainer'] as const,

  /**
   * Liefertermin preview query (`GET /api/v1/auftraege/{id}/liefertermin-vorschau`) — the
   * unverbindlich, as-of-today recalculation shown while the order draft's `lieferterminZustand`
   * is `VORSCHAU` (before submission fixes the actual `liefertermin`).
   *
   * Nested *under* {@link auftraegeKeys.detail} for the same reason as
   * {@link auftraegeKeys.belegdaten}: the preview depends on the order's current product/catalog
   * choice, so every draft mutation (e.g. a product change in Step 1, an Abdruckart change in
   * Step 2) that already invalidates `detail(id)` automatically invalidates this entry too via
   * prefix matching — no mutation needs to remember a second key.
   */
  lieferterminVorschau: (auftragId: string) =>
    [...auftraegeKeys.detail(auftragId), 'liefertermin-vorschau'] as const,
};
